/**
 * Número de destaque: rótulo, valor, unidade e nota.
 *
 * Substitui o `Statistic` do antd. O valor numérico **conta até o número novo** em vez
 * de trocar de repente: na tela ao vivo, onde os valores mudam a cada atualização, a
 * contagem mostra a direção da mudança, que a troca seca esconde.
 *
 * Quem pediu movimento reduzido no sistema recebe o número direto, sem animação.
 */

import { useEffect, type ReactNode } from "react";
import { animate, motion, useMotionValue, useReducedMotion, useTransform } from "motion/react";
import { formatNumber } from "../lib/format.js";
import { TONE_TEXT_CLASS, type Tone } from "../theme.js";

/** Duração da contagem até o valor novo, em segundos. */
const COUNT_DURATION_S = 0.8;

/** Porte do número: o que decide a tela, os de painel e os compactos. */
type StatSize = "hero" | "metric" | "compact";

/** Classes de tamanho de cada porte, responsivas. */
const VALUE_SIZE_CLASS: Readonly<Record<StatSize, string>> = {
  hero: "text-4xl sm:text-5xl",
  metric: "text-2xl sm:text-3xl",
  compact: "text-xl",
};

/** Entradas do número animado. */
interface AnimatedNumberProps {
  readonly value: number;
  readonly precision: number;
}

/** Número que conta a partir do valor anterior até o novo. */
function AnimatedNumber({ value, precision }: AnimatedNumberProps): React.ReactElement {
  const reduceMotion = useReducedMotion();
  const current = useMotionValue(reduceMotion === true ? value : 0);
  const text = useTransform(current, (latest) => formatNumber(latest, precision));

  useEffect(() => {
    if (reduceMotion === true) {
      current.set(value);
      return;
    }
    const controls = animate(current, value, { duration: COUNT_DURATION_S, ease: "easeOut" });
    return () => controls.stop();
  }, [current, value, reduceMotion]);

  return <motion.span>{text}</motion.span>;
}

/** Entradas do número de destaque. */
interface StatProps {
  readonly label: ReactNode;
  readonly value: number | string;
  readonly precision?: number;
  readonly prefix?: string;
  readonly suffix?: string;
  readonly caption?: ReactNode;
  readonly tone?: Tone;
  readonly size?: StatSize;
}

/** Número de destaque com rótulo acima e nota abaixo. */
export function Stat({
  label,
  value,
  precision = 0,
  prefix,
  suffix,
  caption,
  tone = "default",
  size = "metric",
}: StatProps): React.ReactElement {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="text-xs font-medium uppercase tracking-wide text-kumo-subtle">{label}</span>
      <span className={`font-semibold leading-tight ${VALUE_SIZE_CLASS[size]} ${TONE_TEXT_CLASS[tone]}`}>
        {prefix !== undefined && (
          <span className="mr-1 text-base font-medium text-kumo-subtle">{prefix}</span>
        )}
        {typeof value === "number" ? <AnimatedNumber value={value} precision={precision} /> : value}
        {suffix !== undefined && (
          <span className="ml-1 text-base font-medium text-kumo-subtle">{suffix}</span>
        )}
      </span>
      {caption !== undefined && <span className="text-sm text-kumo-subtle">{caption}</span>}
    </div>
  );
}
